import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { apiService } from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';
import { ApplicationStatus, statusConfig } from '../types/application';
import { Activity, activityTypeColors } from '../types/activity';
import {
    Calendar as CalendarIcon,
    MapPin,
    Clock,
    Users,
    CheckCircle,
    XCircle,
    Hourglass,
    UserCheck,
    History,
    Download,
    ExternalLink,
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    X
} from 'lucide-react';


export function MyActivities() {
    const navigate = useNavigate();
    const [withdrawingId, setWithdrawingId] = useState<string | null>(null);
    const { user } = useAuth();
    const [upcomingActivities, setUpcomingActivities] = useState<Activity[]>([]);
    const [pastActivities, setPastActivities] = useState<Activity[]>([]);
    const [appliedActivities, setAppliedActivities] = useState<any[]>([]);
    const [openCalendarDropdown, setOpenCalendarDropdown] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [currentWeekStart, setCurrentWeekStart] = useState(() => {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const diff = today.getDate() - dayOfWeek; // Calculate the Monday of current week
        return new Date(today.setDate(diff));
    });

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpenCalendarDropdown(null);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchUpcomingActivities = async () => {
        const upcomingActivities = await apiService.getComingActivities(user!.id || '');
        // good syntax to learn here
        const activitiesWithParticipants = await Promise.all(upcomingActivities.map(async (activity: Activity) => {
            const participants = await apiService.getParticipationsByActivityId(activity.id);
            return {
                ...activity,
                participants: participants.length,
                date: new Date(activity.dateTime).toISOString().split('T')[0],
                time: new Date(activity.dateTime).toISOString().split('T')[1].split('.')[0],
            };
        }));

        setUpcomingActivities(activitiesWithParticipants.map(activity => ({ ...activity, status: ApplicationStatus.APPROVED })));
    }

    const fetchPastActivities = async () => {
        const pastActivities = await apiService.getPastActivities(user!.id || '');
        setPastActivities(pastActivities);
    }

    const fetchAppliedActivities = async () => {
        // TODO: this is showing all the applications, including approved ones
        // need to filter out the approved ones
        // but do i filter on frontend object or backend query?
        const applications = await apiService.getApplicationsByUserId(user!.id);
        const appliedActivities = await Promise.all(
            applications
                .filter(application => ![ApplicationStatus.APPROVED, ApplicationStatus.WITHDRAWN].includes(application.status))
                .map(application => ({
                    ...application.activity,
                    status: application.status as ApplicationStatus,
                    applicationId: application.id,
                    participants: application.activity.participations.length,
                    location: application.activity.location,
                }))
        )
        setAppliedActivities(appliedActivities)
    }

    useEffect(() => {
        fetchUpcomingActivities();
        fetchPastActivities();
    }, []);

    useEffect(() => {
        fetchAppliedActivities();
    }, []);

    const handleViewDetails = (activityId: string) => {
        navigate(`/activity/${activityId}`);
    };

    const handleQuitActivity = async (activityId: string) => {
        setWithdrawingId(activityId);
        await apiService.quitActivity(activityId);
        setWithdrawingId(null);
        setUpcomingActivities(upcomingActivities.filter(activity => activity.id !== activityId));
    };

    const handleCancelApplication = async (activity: any) => {
        setWithdrawingId(activity.id);
        await apiService.updateApplication(
            activity.applicationId,
            ApplicationStatus.WITHDRAWN,
        );
        await new Promise(resolve => setTimeout(resolve, 1000));
        console.log(`Cancelled application for activity ${activity.id}`);
        setWithdrawingId(null);
    };

    // Calendar-specific functions
    const convertTo24Hour = (time12h: string) => {
        const [time, modifier] = time12h.split(' ');
        let [hours, minutes] = time.split(':');
        if (hours === '12') {
            hours = '00';
        }
        if (modifier === 'PM') {
            hours = (parseInt(hours, 10) + 12).toString();
        }
        return `${hours}:${minutes}`;
    };

    const generateICSContent = (activity: any) => {
        const startDate = new Date(`${activity.date}T${convertTo24Hour(activity.time)}`);
        const endDate = activity.endTime ?
            new Date(`${activity.date}T${convertTo24Hour(activity.endTime)}`) :
            new Date(startDate.getTime() + 2 * 60 * 60 * 1000);

        const formatDate = (date: Date) => {
            return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        };

        return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//ActivityHub//Activity Calendar//EN
BEGIN:VEVENT
UID:${activity.id}@activityhub.com
DTSTAMP:${formatDate(new Date())}
DTSTART:${formatDate(startDate)}
DTEND:${formatDate(endDate)}
SUMMARY:${activity.title}
DESCRIPTION:${activity.description || `${activity.type} activity organized by ${activity.creator}`}
LOCATION:${activity.location}
ORGANIZER:CN=${activity.creator}
STATUS:CONFIRMED
TRANSP:OPAQUE
END:VEVENT
END:VCALENDAR`;
    };

    const handleDownloadICS = (activity: any) => {
        const icsContent = generateICSContent(activity);
        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${activity.title.replace(/\s+/g, '_')}.ics`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleAddToGoogleCalendar = (activity: any) => {
        const startDate = new Date(`${activity.date}T${convertTo24Hour(activity.time)}`);
        const endDate = activity.endTime ?
            new Date(`${activity.date}T${convertTo24Hour(activity.endTime)}`) :
            new Date(startDate.getTime() + 2 * 60 * 60 * 1000);

        const formatGoogleDate = (date: Date) => {
            return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
        };

        const params = new URLSearchParams({
            action: 'TEMPLATE',
            text: activity.title,
            dates: `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`,
            details: activity.description || `${activity.type} activity organized by ${activity.creator}`,
            location: activity.location,
            sprop: 'website:activityhub.com'
        });

        window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank');
    };

    const handleAddToAppleCalendar = (activity: any) => {
        handleDownloadICS(activity);
    };

    // Generate week dates
    const getWeekDates = (startDate: Date) => {
        const dates = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(startDate);
            date.setDate(startDate.getDate() + i);
            dates.push(date);
        }
        return dates;
    };

    const navigateWeek = (direction: 'prev' | 'next') => {
        const newDate = new Date(currentWeekStart);
        newDate.setDate(currentWeekStart.getDate() + (direction === 'next' ? 7 : -7));
        setCurrentWeekStart(newDate);
    };

    const goToCurrentWeek = () => {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const diff = today.getDate() - dayOfWeek;
        setCurrentWeekStart(new Date(today.setDate(diff)));
    };

    const weekDates = getWeekDates(currentWeekStart);
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Get activities for a specific date
    const getActivitiesForDate = (date: Date) => {
        const dateString = date.toLocaleDateString().split('T')[0];
        const activitiesForDate = upcomingActivities.filter(activity => {
            return new Date(activity.dateTime).toLocaleDateString().split('T')[0] === dateString;
        });

        return activitiesForDate;
    };

    const formatMonthYear = (date: Date) => {
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };

    const isToday = (date: Date) => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const renderActivityCard = (activity: any, showApplicationDate = false) => {
        const statusInfo = statusConfig[activity.status as keyof typeof statusConfig];
        const StatusIcon = statusInfo?.icon || CalendarIcon;
        const isPastActivity = new Date(activity.dateTime) < new Date();

        return (
            <Card key={activity.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h3 className="text-lg font-semibold mb-1">{activity.title}</h3>
                            <Badge variant="outline" className="mb-2">
                                {activity.type}
                            </Badge>
                        </div>
                        <Badge className={statusInfo.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusInfo.label}
                        </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                        <div className="flex items-center space-x-2">
                            <CalendarIcon size={16} className="text-muted-foreground" />
                            <span>{new Date(activity.dateTime).toLocaleDateString()}</span>
                        </div>

                        <div className="flex items-center space-x-2">
                            <Clock size={16} className="text-muted-foreground" />
                            <span>{new Date(activity.dateTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
                        </div>

                        <div className="flex items-center space-x-2">
                            <MapPin size={16} className="text-muted-foreground" />
                            <span>{activity.location?.name.split(',')[0]}</span>
                        </div>

                        <div className="flex items-center space-x-2">
                            <Users size={16} className="text-muted-foreground" />
                            <span>{activity.participants}/{activity.maxParticipants} participants</span>
                        </div>
                    </div>

                    {showApplicationDate && activity.appliedDate && (
                        <div className="mb-4 p-3 bg-muted rounded-lg">
                            <p className="text-sm text-muted-foreground">
                                Applied on {new Date(activity.appliedDate).toLocaleDateString()}
                            </p>
                        </div>
                    )}

                    {activity.result && (
                        <div className="mb-4 p-3 bg-muted rounded-lg">
                            <p className="text-sm">
                                <span className="font-medium">Result:</span> {activity.result === 'participated' ? 'Successfully participated' : 'Did not participate'}
                            </p>
                        </div>
                    )}

                    <div className="pt-4 border-t border-border flex flex-wrap gap-2">
                        <Button
                            onClick={() => handleViewDetails(activity.id)}
                            size="sm"
                        >
                            View Details
                        </Button>

                        {activity.status === ApplicationStatus.APPROVED && !isPastActivity && (
                            <div className="relative" ref={openCalendarDropdown === activity.id ? dropdownRef : null}>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setOpenCalendarDropdown(openCalendarDropdown === activity.id ? null : activity.id)}
                                >
                                    <CalendarDays size={14} className="mr-1" />
                                    Add to Calendar
                                </Button>
                                {openCalendarDropdown === activity.id && (
                                    <div
                                        className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1"
                                        style={{ zIndex: 99999 }}
                                    >
                                        <button
                                            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center"
                                            onClick={() => {
                                                handleAddToGoogleCalendar(activity);
                                                setOpenCalendarDropdown(null);
                                            }}
                                        >
                                            <ExternalLink size={14} className="mr-2" />
                                            Google Calendar
                                        </button>
                                        <button
                                            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center"
                                            onClick={() => {
                                                handleAddToAppleCalendar(activity);
                                                setOpenCalendarDropdown(null);
                                            }}
                                        >
                                            <Download size={14} className="mr-2" />
                                            Apple Calendar
                                        </button>
                                        <button
                                            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center"
                                            onClick={() => {
                                                handleDownloadICS(activity);
                                                setOpenCalendarDropdown(null);
                                            }}
                                        >
                                            <Download size={14} className="mr-2" />
                                            Download ICS
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {activity.status === ApplicationStatus.APPROVED && !isPastActivity && (
                            <Button
                                onClick={() => handleQuitActivity(activity.id)}
                                variant="secondary"
                                size="sm"
                                disabled={withdrawingId === activity.id}
                            >
                                {withdrawingId === activity.id ? 'Quiting...' : 'Quit'}
                            </Button>
                        )}

                        {(activity.status === ApplicationStatus.PENDING) && (
                            <Button
                                onClick={() => handleCancelApplication(activity)}
                                variant="secondary"
                                size="sm"
                                disabled={withdrawingId === activity.id}
                            >
                                {withdrawingId === activity.id ? 'Cancelling...' : 'Cancel Application'}
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>
        );
    };

    const upcomingCount = upcomingActivities.length;
    // const pendingCount = pendingApplications.length;
    const appliedCount = appliedActivities.length;
    const historyCount = pastActivities.length;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8">
                <h1 className="text-3xl font-bold mb-2">My Activities</h1>
                <p className="text-muted-foreground">
                    Manage your activity applications, upcoming events, and history
                </p>
            </div>

            {/* Custom Weekly Calendar View */}
            <Card className="mb-8">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <CardTitle className="flex items-center space-x-2">
                                <CalendarDays size={24} />
                                <span>Your Activity Calendar</span>
                            </CardTitle>
                            <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                                <span>{upcomingCount} upcoming</span>
                                <span>•</span>
                                <span>{appliedCount} pending</span>
                                <span>•</span>
                                <span>{historyCount} completed</span>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Button onClick={goToCurrentWeek} variant="outline" size="sm">
                                Today
                            </Button>
                            <Button onClick={() => navigateWeek('prev')} variant="outline" size="sm">
                                <ChevronLeft size={16} />
                            </Button>
                            <span className="px-4 py-2 text-sm font-medium">
                                {formatMonthYear(weekDates[0])}
                                {weekDates[0].getMonth() !== weekDates[6].getMonth() &&
                                    ` - ${formatMonthYear(weekDates[6])}`}
                            </span>
                            <Button onClick={() => navigateWeek('next')} variant="outline" size="sm">
                                <ChevronRight size={16} />
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-7 gap-1 mb-4">
                        {weekDates.map((date, index) => {
                            const dayActivities = getActivitiesForDate(date);
                            const isTodayDate = isToday(date);

                            return (
                                <div key={index} className="min-h-32">
                                    {/* Day header */}
                                    <div className={`text-center py-2 rounded-t-lg border-b ${isTodayDate
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-muted text-muted-foreground'
                                        }`}>
                                        <div className="text-xs font-medium">{dayNames[index]}</div>
                                        <div className={`text-lg ${isTodayDate ? 'font-bold' : ''}`}>
                                            {date.getDate()}
                                        </div>
                                    </div>

                                    {/* Activities for this day */}
                                    <div className="p-2 space-y-1 min-h-24 bg-card border-l border-r border-b rounded-b-lg">
                                        {dayActivities.length > 0 ? (
                                            dayActivities.map(activity => (
                                                <div
                                                    key={activity.id}
                                                    className="group relative cursor-pointer"
                                                    onClick={() => handleViewDetails(activity.id)}
                                                >
                                                    <div className={`p-2 rounded text-xs text-white relative overflow-hidden ${activityTypeColors[activity.type as keyof typeof activityTypeColors] || 'bg-gray-500'
                                                        }`}>
                                                        <div className="font-medium truncate">{activity.title}</div>
                                                        <div className="text-xs opacity-90">{new Date(activity.dateTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>

                                                        {/* Hover tooltip */}
                                                        <div className="absolute bottom-full left-0 mb-2 w-64 p-3 bg-popover text-popover-foreground border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                                                            <div className="font-semibold mb-1">{activity.title}</div>
                                                            <div className="text-sm space-y-1">
                                                                <div className="flex items-center space-x-1">
                                                                    <Clock size={12} />
                                                                    <span>{new Date(activity.dateTime).toLocaleTimeString()}</span>
                                                                </div>
                                                                <div className="flex items-center space-x-1">
                                                                    <MapPin size={12} />
                                                                    <span>{activity.location?.name.split(',')[0]}</span>
                                                                </div>
                                                                <div className="flex items-center space-x-1">
                                                                    <Users size={12} />
                                                                    <span>{activity.participants}/{activity.maxParticipants} joined</span>
                                                                </div>
                                                            </div>
                                                            <div className="flex space-x-1 mt-2">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleAddToGoogleCalendar(activity);
                                                                    }}
                                                                    className="inline-flex items-center px-2 py-1 text-xs bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded border"
                                                                >
                                                                    <ExternalLink size={10} className="mr-1" />
                                                                    Google
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDownloadICS(activity);
                                                                    }}
                                                                    className="inline-flex items-center px-2 py-1 text-xs bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded border"
                                                                >
                                                                    <Download size={10} className="mr-1" />
                                                                    Download
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="h-16 flex items-center justify-center text-xs text-muted-foreground">
                                                No activities
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Quick stats and legend */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pt-4 border-t gap-4">
                        <div className="flex flex-wrap items-center gap-4 text-sm">
                            <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-green-500 rounded"></div>
                                <span>Tennis</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-orange-500 rounded"></div>
                                <span>Basketball</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-purple-500 rounded"></div>
                                <span>Yoga</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-red-500 rounded"></div>
                                <span>Climbing</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                                <span>Swimming</span>
                            </div>
                        </div>
                        <div className="text-sm text-muted-foreground">
                            Click activities for details • Hover for quick actions
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Activity Tabs */}
            <Tabs defaultValue="upcoming" className="space-y-6">
                <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="upcoming">
                        Upcoming ({upcomingCount})
                    </TabsTrigger>
                    <TabsTrigger value="applications">
                        Applications ({appliedCount})
                    </TabsTrigger>
                    <TabsTrigger value="history">
                        History ({historyCount})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="upcoming">
                    <div className="space-y-4">
                        {upcomingActivities.length > 0 ? (
                            upcomingActivities.map(activity => renderActivityCard(activity))
                        ) : (
                            <div className="text-center py-12">
                                <CalendarIcon className="mx-auto mb-4 text-muted-foreground" size={48} />
                                <p className="text-muted-foreground">No upcoming activities.</p>
                                <p className="text-sm text-muted-foreground mt-2">
                                    Apply for activities in the Explore page!
                                </p>
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="applications">
                    <div className="space-y-4">
                        {appliedActivities.length > 0 ? (
                            appliedActivities.map(activity => renderActivityCard(activity, true))
                        ) : (
                            <div className="text-center py-12">
                                <Hourglass className="mx-auto mb-4 text-muted-foreground" size={48} />
                                <p className="text-muted-foreground">No pending applications.</p>
                                <p className="text-sm text-muted-foreground mt-2">
                                    Apply for activities in the Explore page!
                                </p>
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="history">
                    <div className="space-y-4">
                        {pastActivities.length > 0 ? (
                            pastActivities.map(activity => renderActivityCard(activity))
                        ) : (
                            <div className="text-center py-12">
                                <History className="mx-auto mb-4 text-muted-foreground" size={48} />
                                <p className="text-muted-foreground">No activity history yet.</p>
                                <p className="text-sm text-muted-foreground mt-2">
                                    Participate in activities to build your history!
                                </p>
                            </div>
                        )}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}